import { $, $$, closest, setAttributes } from 'utils/dom';
import { uniqueId } from 'utils/helper';
import Bus from 'modules/event-bus';
import Shower from 'modules/shower';

class Navigation {
  constructor(options) {
    this.container = options.container;
    this.overlayToggles = $$('.navigation__overlay-toggle');
    this.overlays = $$('.header__overlay');
    this.menuToggle = $('.btn--burger', this.container);
    this.subMenuToggles = $$('.navigation__toggle-sublevel', this.container);
    this.searchToggle = $('.btn--search', this.container);
    this.mainMenu = $('.navigation--main', this.container);
    this.search = $('.header__searchform', this.container);
    this.overlayIsOpen = false;
    this.focusableElements = $$('a', this.mainMenu);
    this.firstLevelLinks = $$('.navigation__first-level-link', this.mainMenu);
    this.transparentHeader = $('.header--transparent');
    this.logo = $('.start-header__logo');
    this.scrolledDown = false;
    this.activeOverlay = null;
    this.isAnimating = false;
    this.openClass = 'is-open';
  }

  toggleOverlay = toggle => {
    const isOpen = toggle.getAttribute('aria-expanded');
    const target = this.getOverlay(toggle);

    if (isOpen === 'false') {
      this.openOverlay(toggle, target);
    } else {
      this.closeAllOverlays();
    }
  };

  openOverlay = async (toggle, target) => {
    if (this.isAnimating) return;
    await this.closeAllOverlays();

    this.isAnimating = true;
    setTimeout(() => {
      toggle.setAttribute('aria-expanded', true);
      target.setAttribute('aria-hidden', false);
      target.classList.add(this.openClass);
      this.container.classList.add('is-open');
      document.body.classList.add('nav-is-open');
      this.isAnimating = false;
      this.overlayIsOpen = true;
    }, 350);
  };

  closeAllOverlays = () => {
    if (this.isAnimating) return;

    this.isAnimating = true;
    this.overlayToggles.forEach(toggle => {
      toggle.setAttribute('aria-expanded', false);
    });
    this.overlays.forEach(overlay => {
      overlay.setAttribute('aria-hidden', true);
      overlay.classList.remove(this.openClass);
    });
    this.container.classList.remove('is-open');
    document.body.classList.remove('nav-is-open');
    this.isAnimating = false;
    this.overlayIsOpen = false;
  };

  getOverlay = toggle => {
    const handlerAttribute = toggle.getAttribute('aria-controls');
    return $(`#${handlerAttribute}`, this.container);
  };

  toggleSubMenu = toggle => {
    const randomID = uniqueId();
    const target = toggle.parentElement.nextElementSibling;

    setAttributes(toggle, {
      id: `acc-btn-${randomID}`,
      'aria-controls': `acc-tab-${randomID}`,
    });

    setAttributes(target, {
      id: `acc-tab-${randomID}`,
      'aria-labelledby': `acc-btn-${randomID}`,
    });

    new Shower({
      target,
      handler: toggle,
      easing: 'easeInOutQuart',
      duration: 400,
      initOpened: false,
      openClassName: this.openClass,
    }).init();
  };

  handleKeyboardFocusMenu = () => {
    const lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1];

    // handle left- and right arrow keydown on first-level links
    this.firstLevelLinks.forEach(link => {
      link.addEventListener('keydown', e => {
        if (e.key === 'ArrowRight') {
          const nextLink = e.target.parentElement.nextElementSibling
            ? $(
                '.navigation__first-level-link',
                e.target.parentElement.nextElementSibling
              )
            : null;
          if (nextLink) {
            nextLink.focus();
          } else {
            this.firstLevelLinks[0].focus();
          }
        } else if (e.key === 'ArrowLeft') {
          const previousLink = e.target.parentElement.previousElementSibling
            ? $(
                '.navigation__first-level-link',
                e.target.parentElement.previousElementSibling
              )
            : null;
          if (previousLink) {
            previousLink.focus();
          } else {
            this.firstLevelLinks[this.firstLevelLinks.length - 1].focus();
          }
        }
      });
    });

    this.focusableElements.forEach(elem => {
      elem.addEventListener('keydown', e => {
        if (e.key === 'Tab') {
          // focus menu toggle when a user tabs away from the last menu link
          if (document.activeElement === lastFocusableElement) {
            e.preventDefault();
            this.menuToggle.focus();
          }
        } else if (e.key === 'ArrowDown') {
          // handle down-arrow keydowns on all focusable elements inside main menu
          e.preventDefault();
          const isFirstLevelLink = e.target.classList.contains(
            'navigation__first-level-link'
          );
          let nextLink;

          if (isFirstLevelLink) {
            nextLink = e.target.nextElementSibling
              ? $('a', e.target.nextElementSibling.firstElementChild)
              : null;
          } else {
            nextLink = e.target.parentElement.parentElement.nextElementSibling
              ? $('a', e.target.parentElement.parentElement.nextElementSibling)
              : null;
          }

          if (nextLink) {
            nextLink.focus();
          } else {
            const firstLevel = closest(
              e.target,
              '.navigation__first-level-list-item'
            );
            $('a', firstLevel).focus();
          }
        } else if (e.key === 'ArrowUp') {
          // handle up-arrow keydowns on all focusable elements inside main menu
          e.preventDefault();
          const isFirstLevelLink = e.target.classList.contains(
            'navigation__first-level-link'
          );
          let previousLink;
          if (isFirstLevelLink) {
            previousLink = e.target.previousElementSibling
              ? $('a', e.target.previousElementSibling.firstElementChild)
              : null;
          } else {
            previousLink = e.target.parentElement.parentElement
              .previousElementSibling
              ? $(
                  'a',
                  e.target.parentElement.parentElement.previousElementSibling
                )
              : null;
          }

          if (previousLink) {
            previousLink.focus();
          } else {
            if (isFirstLevelLink) {
              if (e.target.nextElementSibling)
                $('a', e.target.nextElementSibling.lastElementChild).focus();
            } else {
              const firstLevel = closest(
                e.target,
                '.navigation__first-level-list-item'
              );
              $('a', firstLevel).focus();
            }
          }
        }
      });
    });
  };

  handleKeyboardFocusSearch = () => {
    const firstFocusableElement = $('input', this.search);
    const lastFocusableElement = $$('a', this.search)[
      $$('a', this.search).length - 1
    ];

    this.searchToggle.addEventListener('keydown', e => {
      if (e.key === 'Tab' && this.overlayIsOpen) {
        // focus menu input when a user tabs away from the search toggle
        e.preventDefault();
        firstFocusableElement && firstFocusableElement.focus();
      }
    });

    lastFocusableElement &&
      lastFocusableElement.addEventListener('keydown', e => {
        if (e.key === 'Tab') {
          // focus searchToggle when a user tabs away from the last focusable element inside the search overlay
          e.preventDefault();
          this.searchToggle.focus();
        }
      });
  };

  closeOnEscapeKey = () => {
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.overlayIsOpen) {
        this.closeAllOverlays(300);
      }
    });
  };

  setAccessibility = () => {
    this.closeOnEscapeKey();
    this.handleKeyboardFocusMenu();
    this.searchToggle && this.handleKeyboardFocusSearch();
  };

  onScroll = () => {
    if (this.logo && window.innerWidth > 1199) {
      const rect = this.logo.getBoundingClientRect();
      if (rect.top <= 0) {
        this.container.classList.remove('header--transparent');
      } else {
        this.container.classList.add('header--transparent');
      }
    } else {
      if (window.scrollY > 0) {
        if (this.scrolledDown) return;
        this.container.classList.remove('header--transparent');
        this.scrolledDown = true;
      } else {
        this.container.classList.add('header--transparent');
        this.scrolledDown = false;
      }
    }
  };

  bindEvents = () => {
    this.transparentHeader &&
      Bus.subscribe('scroll', 'Navigation', this.onScroll);
    this.setAccessibility();
    this.overlayToggles.length &&
      this.overlayToggles.forEach(toggle => {
        toggle.addEventListener('click', this.toggleOverlay.bind(this, toggle));
      });
    this.subMenuToggles.length &&
      this.subMenuToggles.forEach(toggle => {
        this.toggleSubMenu(toggle);
      });
  };

  init = () => {
    this.bindEvents();
  };
}

const navigation = $('.header');
const navigationToggle = $('#main-menu-btn');
navigation &&
  navigationToggle &&
  new Navigation({
    container: navigation,
  }).init();
